/* Admin → My Profile (iter-32)
 *
 * Self-service surface for the signed-in admin:
 *   - Identity + last login
 *   - Rotation status (with overdue banner at 90+ days)
 *   - Change-password form (re-states current password)
 *   - List of own active sessions with revoke buttons
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { KeyRound, ShieldAlert, ShieldCheck, Monitor, Trash2, Clock, RefreshCw, Mail, Smartphone, Fingerprint, Grid3x3 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { PanelHeader } from './_shared';

function relativeTime(iso) {
  if (!iso) return '—';
  try {
    const then = new Date(iso);
    const diffSec = (then.getTime() - Date.now()) / 1000; // negative=past
    const abs = Math.abs(diffSec);
    let s;
    if (abs < 60)          s = `${Math.floor(abs)}s`;
    else if (abs < 3600)   s = `${Math.floor(abs / 60)}m`;
    else if (abs < 86400)  s = `${Math.floor(abs / 3600)}h`;
    else if (abs < 86400 * 30) s = `${Math.floor(abs / 86400)}d`;
    else                   return then.toLocaleDateString();
    return diffSec >= 0 ? `in ${s}` : `${s} ago`;
  } catch { return iso; }
}

export default function ProfilePanel() {
  const [me, setMe] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [email, setEmail] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [mobileLock, setMobileLock] = useState(null);
  const [mobileLockBusy, setMobileLockBusy] = useState(false);
  const [newPin, setNewPin] = useState('');
  const supportsWebauthn = typeof window !== 'undefined' && browserSupportsWebAuthn();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [meR, sessR, lockR] = await Promise.all([
        adminApi.me(),
        adminApi.mySessions().catch(() => ({ sessions: [] })),
        adminApi.mobileLockMe().catch(() => null),
      ]);
      setMe(meR);
      setEmail(meR.email || '');
      setSessions(sessR.sessions || []);
      setMobileLock(lockR);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load profile.');
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleMobileLock = async (enabled) => {
    setMobileLockBusy(true);
    try {
      await adminApi.mobileLockToggle(enabled);
      toast.success(enabled ? 'Mobile lock enabled.' : 'Mobile lock disabled.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update mobile lock.');
    } finally { setMobileLockBusy(false); }
  };

  const registerDevice = async () => {
    setMobileLockBusy(true);
    try {
      const options = await adminApi.mobileLockRegisterOptions();
      const credential = await startRegistration({ optionsJSON: options });
      const label = window.prompt('Name this device (e.g. "My iPhone")', 'My phone') || 'My phone';
      await adminApi.mobileLockRegisterVerify(credential, label);
      toast.success('Device registered for biometric unlock.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not register this device.');
    } finally { setMobileLockBusy(false); }
  };

  const removeDevice = async (credentialId) => {
    try {
      await adminApi.mobileLockDeleteDevice(credentialId);
      toast.success('Device removed.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove device.');
    }
  };

  const savePin = async () => {
    if (newPin.length < 4) { toast.error('PIN must be at least 4 digits.'); return; }
    setMobileLockBusy(true);
    try {
      await adminApi.mobileLockSetPin(newPin);
      toast.success('PIN saved.');
      setNewPin('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save PIN.');
    } finally { setMobileLockBusy(false); }
  };

  const clearPin = async () => {
    setMobileLockBusy(true);
    try {
      await adminApi.mobileLockClearPin();
      toast.success('PIN removed.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove PIN.');
    } finally { setMobileLockBusy(false); }
  };

  const saveEmail = async () => {
    if (email && !email.includes('@')) { toast.error('Enter a valid email address.'); return; }
    setEmailBusy(true);
    try {
      await adminApi.setMyEmail(email.trim());
      toast.success('Recovery/notification email saved.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save email.');
    } finally { setEmailBusy(false); }
  };

  const submitChange = async () => {
    if (pwd.next.length < 10) { toast.error('New password must be at least 10 characters.'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('New passwords do not match.'); return; }
    setBusy(true);
    try {
      await adminApi.changeMyPassword(pwd.current, pwd.next);
      toast.success('Password updated. Other sessions still work — revoke them below if needed.');
      setPwd({ current: '', next: '', confirm: '' });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Password change failed.');
    } finally { setBusy(false); }
  };

  const revoke = async (token) => {
    try {
      await adminApi.revokeSession(token);
      toast.success('Session revoked.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to revoke.');
    }
  };

  const revokeAllOthers = async () => {
    const others = sessions.filter(s => !s.is_current && s.token_full);
    if (!others.length) { toast.info('No other sessions to revoke.'); return; }
    setBusy(true);
    try {
      await Promise.all(others.map(s => adminApi.revokeSession(s.token_full)));
      toast.success(`Revoked ${others.length} other session${others.length === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Some sessions could not be revoked.');
    } finally { setBusy(false); }
  };

  if (!me) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center text-[12px] text-muted-foreground" data-testid="admin-profile-loading">
        Loading profile…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-profile-panel">
      <PanelHeader
        title="My profile"
        subtitle="Identity, password, and active sessions for the admin you're currently signed in as."
        onRefresh={load}
      />

      {/* Identity card */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Signed in as</div>
            <div className="mt-0.5 text-[18px] font-semibold" data-testid="admin-profile-username">
              {me.username}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3 text-[12px]">
              <div>
                <div className="text-muted-foreground">Last login</div>
                <div className="font-mono-stat">{relativeTime(me.last_login_at)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Password rotated</div>
                <div className="font-mono-stat">{relativeTime(me.rotated_at)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Session expires (idle)</div>
                <div className="font-mono-stat">{relativeTime(me.session?.expires_at)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Session hard expiry</div>
                <div className="font-mono-stat">{relativeTime(me.session?.hard_expires_at)}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            {me.must_rotate
              ? <Badge variant="destructive" className="gap-1" data-testid="admin-profile-must-rotate">
                  <ShieldAlert className="h-3 w-3" /> Default password — please rotate
                </Badge>
              : <Badge className="gap-1 bg-emerald-100 text-emerald-900 border border-emerald-300">
                  <ShieldCheck className="h-3 w-3" /> Password set
                </Badge>}
            {me.rotation_overdue && (
              <Badge variant="destructive" className="gap-1" data-testid="admin-profile-overdue">
                <Clock className="h-3 w-3" /> Rotation overdue ({me.days_since_rotation}d)
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Recovery / notification email */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
          <Mail className="h-4 w-4" /> Recovery &amp; notification email
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Used to deliver your password-reset link and any admin notification emails (weekly digest, model-catalog alerts, etc.). Not shown to anyone else.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div className="min-w-[240px] flex-1">
            <Label className="text-[11px]">Email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                   placeholder="you@yourdomain.com" data-testid="admin-profile-email-input" />
          </div>
          <Button onClick={saveEmail} disabled={emailBusy} data-testid="admin-profile-save-email-btn" className="gap-1.5">
            <Mail className="h-3.5 w-3.5" /> {emailBusy ? 'Saving…' : 'Save email'}
          </Button>
        </div>
      </div>

      {/* Change password */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center gap-1.5 text-[13px] font-semibold">
          <KeyRound className="h-4 w-4" /> Change my password
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          Confirm your current password to set a new one. Other active sessions stay logged in — revoke them below if you don&apos;t trust the device.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label className="text-[11px]">Current password</Label>
            <Input type="password" data-testid="admin-profile-current-pw"
                   value={pwd.current} onChange={e => setPwd({ ...pwd, current: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">New password (min 10)</Label>
            <Input type="password" data-testid="admin-profile-new-pw"
                   value={pwd.next} onChange={e => setPwd({ ...pwd, next: e.target.value })} />
          </div>
          <div>
            <Label className="text-[11px]">Confirm new password</Label>
            <Input type="password" data-testid="admin-profile-confirm-pw"
                   value={pwd.confirm} onChange={e => setPwd({ ...pwd, confirm: e.target.value })} />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={submitChange} disabled={busy || !pwd.current || !pwd.next}
                  data-testid="admin-profile-change-pw-btn" className="gap-1.5">
            <KeyRound className="h-3.5 w-3.5" /> {busy ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </div>

      {/* Mobile Staff biometric lock */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel" data-testid="admin-profile-mobile-lock-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5 text-[13px] font-semibold">
            <Smartphone className="h-4 w-4" /> Mobile Lock
          </div>
          <Switch
            checked={!!mobileLock?.enabled}
            disabled={mobileLockBusy || !mobileLock}
            onCheckedChange={toggleMobileLock}
            data-testid="admin-profile-mobile-lock-toggle"
          />
        </div>
        <p className="mt-1 text-[11px] text-muted-foreground">
          When enabled, anyone opening <code>/admin</code> from a phone has to pass Face ID/Touch ID or a PIN first, before the real sign-in form even shows. Off by default — this is a device-level convenience gate on top of your real password, not a replacement for it.
        </p>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="rounded-sm border border-border p-3">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <Fingerprint className="h-3.5 w-3.5" /> Biometric devices
            </div>
            <div className="mt-2 space-y-1.5">
              {(mobileLock?.devices || []).length === 0 && (
                <p className="text-[11px] text-muted-foreground">No devices registered yet.</p>
              )}
              {(mobileLock?.devices || []).map((d) => (
                <div key={d.credential_id} className="flex items-center justify-between gap-2 rounded-sm bg-panel-header/30 px-2 py-1 text-[11.5px]"
                     data-testid={`admin-profile-mobile-device-${d.credential_id}`}>
                  <span className="truncate">{d.device_label}</span>
                  <button type="button" onClick={() => removeDevice(d.credential_id)}
                          data-testid={`admin-profile-mobile-device-remove-${d.credential_id}`}
                          className="text-destructive hover:underline">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-2 w-full gap-1.5" onClick={registerDevice}
                    disabled={mobileLockBusy || !supportsWebauthn} data-testid="admin-profile-mobile-register-btn">
              <Fingerprint className="h-3.5 w-3.5" /> Register this device
            </Button>
            {!supportsWebauthn && (
              <p className="mt-1 text-[10.5px] text-muted-foreground">This browser doesn't support biometric registration — use a PIN instead.</p>
            )}
          </div>

          <div className="rounded-sm border border-border p-3">
            <div className="flex items-center gap-1.5 text-[12px] font-semibold">
              <Grid3x3 className="h-3.5 w-3.5" /> PIN fallback
            </div>
            {mobileLock?.has_pin ? (
              <>
                <p className="mt-2 text-[11px] text-muted-foreground">A PIN is set.</p>
                <Button size="sm" variant="ghost" className="mt-2 w-full gap-1.5 text-destructive" onClick={clearPin}
                        disabled={mobileLockBusy} data-testid="admin-profile-mobile-clear-pin-btn">
                  <Trash2 className="h-3.5 w-3.5" /> Remove PIN
                </Button>
              </>
            ) : (
              <>
                <Input type="password" inputMode="numeric" placeholder="4-12 digit PIN" className="mt-2"
                       value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                       data-testid="admin-profile-mobile-pin-input" />
                <Button size="sm" className="mt-2 w-full gap-1.5" onClick={savePin}
                        disabled={mobileLockBusy || newPin.length < 4} data-testid="admin-profile-mobile-save-pin-btn">
                  <Grid3x3 className="h-3.5 w-3.5" /> Save PIN
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Active sessions */}
      <div className="rounded-md border border-border bg-card p-4 shadow-panel">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-1.5 text-[13px] font-semibold">
              <Monitor className="h-4 w-4" /> My active sessions
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">
              Each browser/device you&apos;ve logged in from gets its own session. Sessions slide forward while in use; an idle session expires after 60 minutes. Revoke any you don&apos;t recognise.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="gap-1.5 text-destructive"
                         data-testid="admin-revoke-all-others-btn"
                         disabled={busy || sessions.filter(s => !s.is_current).length === 0}>
                  <Trash2 className="h-3.5 w-3.5" /> Revoke all others
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Revoke every session except this tab?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Every device or browser tab logged in to this admin account will be signed out immediately. This tab stays active.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={revokeAllOthers} data-testid="admin-revoke-all-others-confirm">
                    Revoke all others
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button size="sm" variant="ghost" onClick={load} className="gap-1.5" data-testid="admin-profile-refresh-sessions">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </Button>
          </div>
        </div>
        <div className="mt-3 overflow-hidden rounded-sm border border-border">
          <table className="w-full text-[12px]">
            <thead className="bg-panel-header/60 text-[11px] text-muted-foreground">
              <tr className="text-left">
                <th className="px-2 py-1.5">Session</th>
                <th className="px-2 py-1.5">Last activity</th>
                <th className="px-2 py-1.5">Expires (idle)</th>
                <th className="px-2 py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr><td colSpan={4} className="px-2 py-4 text-center text-muted-foreground">No active sessions.</td></tr>
              )}
              {sessions.map((s) => (
                <tr key={s.token_full} className="border-t border-border" data-testid={`admin-session-row-${s.token_preview}`}>
                  <td className="px-2 py-1.5">
                    <span className="font-mono-stat">{s.token_preview}</span>
                    {s.is_current && (
                      <Badge className="ml-2 border border-blue-300 bg-blue-100 text-blue-900">this tab</Badge>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{relativeTime(s.last_activity_at)}</td>
                  <td className="px-2 py-1.5">{relativeTime(s.expires_at)}</td>
                  <td className="px-2 py-1.5 text-right">
                    {s.is_current ? (
                      <span className="text-[11px] text-muted-foreground">
                        Use &ldquo;Sign out&rdquo; to end this session
                      </span>
                    ) : (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="h-7 text-destructive gap-1.5"
                                data-testid={`admin-revoke-session-btn-${s.token_preview}`}>
                          <Trash2 className="h-3.5 w-3.5" /> Revoke
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revoke this session?</AlertDialogTitle>
                          <AlertDialogDescription>
                            The device using this session will be signed out immediately.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => revoke(s.token_full)}
                                              data-testid={`admin-revoke-session-confirm-${s.token_preview}`}>
                            Revoke session
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
